import React from 'react';
import { permissionTree } from './permissionTree';
import Node from './Node';
import { viewEditWidth } from './utils';
import { getIntl } from '../../../utils/intl';
import { ConfigurableTable } from '../../v2BaseComponents/ConfigurationTable';
import { useCollaboratorEditWarningPrompt } from './useCollaboratorEditWarningPrompt';
import Stack from '../../v2BaseComponents/Stack';
import Alert from '../../v2BaseComponents/Alert';
import { ABAC } from '../../../primary-record-types';

const intl = getIntl('permissions-input-root', {
    access: 'Permissions',
    view: 'View',
    edit: 'Edit',
    collaboratorEditContent:
        'Adding the edit permission for collaborators will allow them to invite or remove other collaborators and give others access to view this profile.',
    collaboratorEditTitle: 'Collaborator Edit Enabled',
});

export type PermissionsInputOwnProps = {
    value: ABAC.AppPermissions;
    onChange: (value: ABAC.AppPermissions) => void;
    mostPermissiveAppPermissions?: ABAC.AppPermissions;
    isDisabled?: boolean;
    accessLabel?: string;
    editLabel?: string;
    showEditCollaboratorWarning?: boolean;
    initialAppPermissions: ABAC.AppPermissions;
};

export type PermissionsInputProps = PermissionsInputOwnProps;

const PermissionsInput: React.FC<PermissionsInputProps> = (props) => {
    const { Prompt } = useCollaboratorEditWarningPrompt({
        appPermissions: props.value,
        onChange: props.onChange,
        initialAppPermissions: props.initialAppPermissions,
    });
    return (
        <>
            <Stack vertical fullWidth space={2}>
                <ConfigurableTable
                    title={intl('access')}
                    checkboxTitles={[intl('view'), intl('edit')]}
                    checkboxWidth={viewEditWidth}
                    isDisabled={props.isDisabled}
                >
                    {permissionTree.map((node, index) => (
                        <Node
                            value={props.value}
                            onChange={props.onChange}
                            node={node}
                            isDisabled={props.isDisabled}
                            key={index}
                            isRoot={true}
                            isLast={false}
                            mostPermissiveAppPermissions={props.mostPermissiveAppPermissions}
                        />
                    ))}
                </ConfigurableTable>
                {props.showEditCollaboratorWarning && props.value.collaborators === 'edit' ? (
                    <Alert
                        type="info"
                        title={intl('collaboratorEditTitle')}
                        message={intl('collaboratorEditContent')}
                        testID="collaborator-edit-warning"
                    />
                ) : null}
            </Stack>
            {Prompt}
        </>
    );
};

export default React.memo(PermissionsInput);
